import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import ComponentCard from "../../common/ComponentCard";
import Label from "../Label";
import Input from "../input/InputField";
import Select from "../Select";
import TextArea from "../input/TextArea";
import Switch from "../switch/Switch";
import Radio from "../../../components/form/input/Radio";
import Button from "../../ui/button/Button";
import axios from "axios";
import { authService } from "../../../services/authService";
import TagsInput from "../TagsInput";
import InitialInstallmentSelect from "./InitialInstallmentSelect";
import { INITIAL_INSTALLMENTS_MAX, clampInitialInstallments, parseSettingsData, readInitialInstallments } from "../../../utils/initialInstallments";
import api from "../../../services/api";

interface OfferFormData {
    name: string;
    description: string;
    anchoringPrice: number;
    anchoringPriceFormatted: string;
    price: number;
    currency: string;
    checkoutLanguage: string;
    priceFormatted: string;
    status: string;
    paymentMethod: string;
    installments: string;
    enableFunnel: boolean;
    funnelTags: string[];
    funnelOrigin: string;
    funnelMedium: string;
    funnelCampaign: string;
    funnelTerm: string;
    funnelContent: string;
    sck: string;
    paymentMethods: {
        pix: boolean;
        boleto: boolean;
        creditCard: boolean;
    };
}

interface EditOfferFormProps {
    productId?: string;
    offerId?: string;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
}

// Valor em reais (19.9) -> centavos em texto ("1990"). Arredonda porque 19.9 * 100
// dá 1989.9999999999998 no ponto flutuante, e o formatPrice, que tira tudo que não é
// dígito, leria 19899999999999998 centavos (e travaria no preço máximo).
const toCentsString = (value: unknown): string => {
    const n = Number(value);
    return Number.isFinite(n) ? String(Math.round(n * 100)) : '0';
};

// Função para formatar o preço em tempo real no formato brasileiro
const formatPrice = (value: string): { formattedValue: string, numericValue: number } => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');

    // Converte para número (em centavos)
    let centsValue = parseInt(numbers) || 0;

    // Converte centavos para real
    let realValue = centsValue / 100;

    if (realValue > MAX_OFFER_PRICE) {
        realValue = MAX_OFFER_PRICE;
        centsValue = MAX_OFFER_PRICE * 100;
    }

    // Formata no padrão brasileiro
    const formatter = new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return {
        formattedValue: formatter.format(realValue),
        numericValue: realValue
    };
};

const currencyOptions = [
    { value: "BRL", label: "🇧🇷 R$ (BRL)" }
];

const languageOptions = [
    { value: "pt", label: "🇧🇷 Português" },
    { value: "en", label: "🇺🇸 English" },
    { value: "es", label: "🇪🇸 Español" },
    { value: "fr", label: "🇫🇷 Français" },
    { value: "de", label: "🇩🇪 Deutsch" },
    { value: "it", label: "🇮🇹 Italiano" },
    { value: "nl", label: "🇳🇱 Nederlands" },
    { value: "ja", label: "🇯🇵 日本語 (Japonês)" },
    { value: "zh", label: "🇨🇳 中文 (Chinês)" },
    { value: "tr", label: "🇹🇷 Türkçe" },
    { value: "ru", label: "🇷🇺 Русский (Russo)" },
    { value: "ar", label: "🇸🇦 العربية (Árabe)" },
    { value: "he", label: "🇮🇱 עִבְרִית (Hebraico)" },
    { value: "pl", label: "🇵🇱 Polski" },
    { value: "hi", label: "🇮🇳 हिन्दी (Hindi)" }
];

const MAX_OFFER_PRICE = 10_000_000;


const recurringCycleOptions = [
    { value: 'monthly', label: 'Mensal' },
    { value: 'quarterly', label: 'Trimestral' },
    { value: 'semiannual', label: 'Semestral' },
    { value: 'annual', label: 'Anual' },
];

export default function EditOfferForm({ productId, offerId, onSuccess, onError }: EditOfferFormProps) {
    const [formData, setFormData] = useState<OfferFormData>({
        name: "",
        description: "",
        anchoringPrice: 0,
        anchoringPriceFormatted: "0,00",
        price: 0,
        currency: 'BRL',
        checkoutLanguage: 'pt',
        priceFormatted: "0,00",
        status: "active",
        paymentMethod: "",
        installments: "1",
        enableFunnel: false,
        funnelTags: [],
        funnelOrigin: "",
        funnelMedium: "",
        funnelCampaign: "",
        funnelTerm: "",
        funnelContent: "",
        sck: "",
        paymentMethods: {
            pix: false,
            boleto: false,
            creditCard: false
        }
    });

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");
    const [billingType, setBillingType] = useState<'single' | 'recurring'>('single');
    const [recurringFrequency, setRecurringFrequency] = useState('');
    const [firstChargeAmount, setFirstChargeAmount] = useState('');
    const [funnelOptions, setFunnelOptions] = useState<{ value: string, label: string }[]>([]);
    const [hasFirstChargeDifferent, setHasFirstChargeDifferent] = useState(false);
    const [firstChargeAmountFormatted, setFirstChargeAmountFormatted] = useState('');
    const [currency, setCurrency] = useState('BRL');
    const [checkoutLanguage, setCheckoutLanguage] = useState('pt');
    // Parcela exibida ao abrir o checkout (settingsData.initialInstallments) + demais chaves do settingsData
    const [initialInstallments, setInitialInstallments] = useState<string>(INITIAL_INSTALLMENTS_MAX);
    const [existingSettingsData, setExistingSettingsData] = useState<Record<string, unknown>>({});
    const [fetchLoading, setFetchLoading] = useState(false);

    // Buscar dados da oferta quando o componente é montado
    useEffect(() => {
        const fetchOfferData = async () => {
            if (!productId || !offerId) return;

            setFetchLoading(true);
            setApiError("");

            try {
                // Buscar informações da oferta
                const offerResponse = await axios.get(
                    `${import.meta.env.VITE_API_URL}/api/products/${productId}/offers/${offerId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${authService.getToken()}`
                        }
                    }
                );

                const offerData = offerResponse.data.offer;

                // Buscar todos os funis da empresa
                const companyId = authService.getCompanyId();
                if (!companyId) return;

                // Buscar tanto os funis da empresa quanto os funis dessa oferta específica
                const [companyFunnelsResponse, offerFunnelsResponse] = await Promise.all([
                    api.get(`/api/funnels/company/${companyId}`),
                    api.get(`/api/funnels/offer/${offerId}`)
                ]);

                // Processar funis da empresa (todas as opções disponíveis)
                if (companyFunnelsResponse.data && companyFunnelsResponse.data.data) {
                    const options = companyFunnelsResponse.data.data.map((funnel: any) => ({
                        value: funnel.name,
                        label: funnel.name
                    }));
                    setFunnelOptions(options);
                }

                // Processar funis associados à oferta (já selecionados)
                let selectedFunnelTags: string[] = [];
                let funnelSource: string = "";
                let funnelMedium: string = "";
                let funnelCampaign: string = "";
                let funnelTerm: string = "";
                let funnelContent: string = "";
                let funnelSck: string = "";
                if (offerFunnelsResponse.data && offerFunnelsResponse.data.data) {
                    selectedFunnelTags = offerFunnelsResponse.data.data.map((funnel: any) => funnel.name);
                    // Pegar os dados do primeiro funil (se existir)
                    if (offerFunnelsResponse.data.data.length > 0) {
                        const firstFunnel = offerFunnelsResponse.data.data[0];
                        funnelSource = firstFunnel.source || "";
                        funnelMedium = firstFunnel.medium || "";
                        funnelCampaign = firstFunnel.campaign || "";
                        funnelTerm = firstFunnel.term || "";
                        funnelContent = firstFunnel.content || "";
                        funnelSck = firstFunnel.sck || "";
                    }
                }

                if (offerData) {
                    // Determinar o método de pagamento com base nos valores booleanos
                    let paymentMethod = "creditCard_pix_boleto"; // Padrão

                    if (offerData.paymentMethods) {
                        const { creditCard, pix, boleto } = offerData.paymentMethods;

                        if (creditCard && pix && boleto) {
                            paymentMethod = "creditCard_pix_boleto";
                        } else if (creditCard && pix) {
                            paymentMethod = "creditCard_pix";
                        } else if (creditCard && boleto) {
                            paymentMethod = "creditCard_boleto";
                        } else if (creditCard) {
                            paymentMethod = "creditCard";
                        }
                    }

                    // Formatar preços para exibição
                    const { formattedValue: priceFormatted, numericValue: price } = formatPrice(toCentsString(offerData.price));
                    const { formattedValue: anchoringPriceFormatted, numericValue: anchoringPrice } = formatPrice(toCentsString(offerData.anchoringPrice));

                    // Configurar tipo de cobrança e dados relacionados
                    const offerBillingType = offerData.billingType || 'single';
                    setBillingType(offerBillingType as 'single' | 'recurring');

                    // Para cobranças recorrentes, buscar dados do paymentPlan
                    if (offerBillingType === 'recurring' && offerData.paymentPlan) {
                        // Obter frequência do paymentPlan
                        setRecurringFrequency(offerData.paymentPlan.frequency || '');

                        // Verificar se há valor diferente na primeira cobrança
                        const firstPaymentAmount = offerData.paymentPlan.firstPaymentAmount;
                        const hasFirstDifferent = firstPaymentAmount !== null && firstPaymentAmount !== undefined && firstPaymentAmount !== offerData.price;
                        setHasFirstChargeDifferent(hasFirstDifferent);

                        // Se houver valor diferente, configurar
                        if (hasFirstDifferent && firstPaymentAmount) {
                            setFirstChargeAmount(firstPaymentAmount.toString());

                            // Formatar valor para exibição - multiplicamos por 100 porque formatPrice espera centavos
                            const { formattedValue } = formatPrice(Math.round(parseFloat(firstPaymentAmount) * 100).toString());
                            setFirstChargeAmountFormatted(formattedValue);
                        }
                    }

                    // Configurar moeda e idioma
                    setCurrency(offerData.currency || 'BRL');
                    setExistingSettingsData(parseSettingsData(offerData.settingsData));
                    setInitialInstallments(readInitialInstallments(offerData.settingsData));
                    setCheckoutLanguage(offerData.checkoutLanguage || 'pt');

                    setFormData({
                        name: offerData.name || "",
                        description: offerData.description || "",
                        anchoringPrice: anchoringPrice,
                        anchoringPriceFormatted: anchoringPriceFormatted,
                        price: price,
                        currency: offerData.currency || "BRL",
                        checkoutLanguage: offerData.checkoutLanguage || "pt",
                        priceFormatted: priceFormatted,
                        status: offerData.active ? "active" : "inactive",
                        paymentMethod: paymentMethod,
                        installments: offerData.maxInstallments?.toString() || "1",
                        paymentMethods: offerData.paymentMethods,
                        // Ativar o funil se existirem tags associadas à oferta
                        enableFunnel: (selectedFunnelTags && selectedFunnelTags.length > 0) || (offerData.funnelTags && offerData.funnelTags.length > 0) || offerData.enableFunnel || false,
                        funnelTags: selectedFunnelTags,
                        funnelOrigin: funnelSource,
                        funnelMedium: funnelMedium,
                        funnelCampaign: funnelCampaign,
                        funnelTerm: funnelTerm,
                        funnelContent: funnelContent,
                        sck: funnelSck
                    });
                }

            } catch (error: any) {
                console.error("Erro ao buscar dados da oferta:", error);
                setApiError(error.response?.data?.message || "Erro ao carregar dados da oferta. Tente novamente.");
            } finally {
                setFetchLoading(false);
            }
        };

        fetchOfferData();
    }, [productId, offerId]);

    // Opções de pagamento baseadas na moeda selecionada
    const getBrazilPaymentMethods = () => [
        { value: "creditCard_pix_boleto", label: "Cartão de Crédito, Pix e Boleto" },
        { value: "creditCard_pix", label: "Cartão de Crédito e Pix" },
        { value: "creditCard_boleto", label: "Cartão de Crédito e Boleto" },
        { value: "creditCard", label: "Apenas Cartão de Crédito" }
    ];

    const getInternationalPaymentMethods = () => [
        { value: "creditCard", label: "Apenas Cartão de Crédito" }
    ];

    // Determinar métodos de pagamento com base na moeda
    const productPaymentMethods = currency === 'BRL'
        ? getBrazilPaymentMethods()
        : getInternationalPaymentMethods();

    // Opções de parcelamento baseadas na moeda selecionada
    const getBrazilInstallments = () => [
        { value: "1", label: "À vista" },
        { value: "2", label: "Até 2x" },
        { value: "3", label: "Até 3x" },
        { value: "4", label: "Até 4x" },
        { value: "5", label: "Até 5x" },
        { value: "6", label: "Até 6x" },
        { value: "7", label: "Até 7x" },
        { value: "8", label: "Até 8x" },
        { value: "9", label: "Até 9x" },
        { value: "10", label: "Até 10x" },
        { value: "11", label: "Até 11x" },
        { value: "12", label: "Até 12x" }
    ];

    const getInternationalInstallments = () => [
        { value: "1", label: "À vista" }
    ];

    // Determinar opções de parcelamento com base na moeda
    const productPaymentInstallments = currency === 'BRL'
        ? getBrazilInstallments()
        : getInternationalInstallments();

    const handlePaymentMethodChange = (value: string) => {
        // Atualizar os valores booleanos com base na seleção
        let paymentMethods = {
            creditCard: false,
            pix: false,
            boleto: false
        };

        switch (value) {
            case "creditCard_pix_boleto":
                paymentMethods = { creditCard: true, pix: true, boleto: true };
                break;
            case "creditCard_pix":
                paymentMethods = { creditCard: true, pix: true, boleto: false };
                break;
            case "creditCard_boleto":
                paymentMethods = { creditCard: true, pix: false, boleto: true };
                break;
            case "creditCard":
                paymentMethods = { creditCard: true, pix: false, boleto: false };
                break;
        }

        setFormData({
            ...formData,
            paymentMethod: value,
            paymentMethods: paymentMethods
        });

        // Limpar o erro se o campo foi preenchido
        if (errors.paymentMethod && value) {
            setErrors({
                ...errors,
                paymentMethod: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleInstallmentsChange = (value: string) => {
        setFormData({ ...formData, installments: value });

        // Limpar o erro se o campo foi preenchido
        if (errors.installments && value) {
            setErrors({
                ...errors,
                installments: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;

        // Limpar o erro específico quando o campo é modificado
        if (errors[id]) {
            setErrors({
                ...errors,
                [id]: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");

        // Mapeamento de IDs para estrutura do formData
        switch (id) {
            case "inputName":
                setFormData({ ...formData, name: value });
                break;
            case "inputAnchoringPrice":
                const { formattedValue: anchoringFormatted, numericValue: anchoringNumeric } = formatPrice(value);
                setFormData({
                    ...formData,
                    anchoringPrice: anchoringNumeric,
                    anchoringPriceFormatted: anchoringFormatted
                });
                break;
            case "inputPrice":
                const { formattedValue, numericValue } = formatPrice(value);
                setFormData({
                    ...formData,
                    price: numericValue,
                    priceFormatted: formattedValue
                });
                break;
            default:
                break;
        }
    };

    const handleTextAreaChange = (value: string) => {
        setFormData({ ...formData, description: value });

        // Limpar o erro se o campo foi preenchido
        if (errors.description && value) {
            setErrors({
                ...errors,
                description: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleSwitchChange = (checked: boolean) => {
        setFormData({
            ...formData,
            status: checked ? "active" : "inactive"
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleFunnelSwitchChange = (checked: boolean) => {
        setFormData({
            ...formData,
            enableFunnel: checked,
            // Se desativar o funil, limpar as tags e origem
            funnelTags: checked ? formData.funnelTags : [],
            funnelOrigin: checked ? formData.funnelOrigin : ""
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    // Manipulador para atualizar as tags do funil
    // Buscar os funnels da empresa quando o componente carregar
    useEffect(() => {
        // Não buscar funnels se não houver produto ou oferta selecionados
        // Isso será feito diretamente na função fetchOfferData
        if (!productId || !offerId) {
            const fetchFunnels = async () => {
                try {
                    const companyId = authService.getCompanyId();
                    if (!companyId) return;

                    const response = await api.get(`/api/funnels/company/${companyId}`);

                    if (response.data && response.data.data) {
                        // Transformar os funnels em options para o TagsInput
                        const options = response.data.data.map((funnel: any) => ({
                            value: funnel.name,
                            label: funnel.name
                        }));
                        setFunnelOptions(options);
                    }
                } catch (error) {
                    console.error('Erro ao buscar funnels:', error);
                }
            };

            fetchFunnels();
        }
    }, [productId, offerId]);

    const handleTagsChange = (tags: string[]) => {
        setFormData(prev => ({
            ...prev,
            funnelTags: tags
        }));

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");

        // Note: Quando o usuário altera as tags, estamos apenas mantendo os valores no formData
        // A atualização na tabela offerFunnel será feita quando o usuário salvar o formulário
    };

    const handleGoBack = () => {
        navigate(`/products/edit/${productId}?tab=ofertas`);
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.inputName = "O nome da oferta é obrigatório";
        }

        if (!formData.description.trim()) {
            newErrors.description = "A descrição da oferta é obrigatória";
        }

        if (!formData.price || formData.price <= 0) {
            newErrors.inputPrice = "O preço deve ser maior que zero";
        } else if (formData.price > MAX_OFFER_PRICE) {
            newErrors.inputPrice = "O valor máximo permitido é R$ 10.000.000,00";
        }

        if (!formData.paymentMethod) {
            newErrors.paymentMethod = "A forma de pagamento é obrigatória";
        }

        if (!formData.installments) {
            newErrors.installments = "O parcelamento é obrigatório";
        }

        if (!currency) {
            newErrors.currency = "A moeda é obrigatória";
        }

        if (!checkoutLanguage) {
            newErrors.checkoutLanguage = "O idioma do checkout é obrigatório";
        }

        // Validar frequência apenas se o tipo de cobrança for recorrente
        if (billingType === 'recurring' && !recurringFrequency) {
            newErrors.recurringFrequency = "A frequência da assinatura é obrigatória";
        }

        // Validar valor da primeira cobrança apenas se for diferente e estiver ativo
        if (billingType === 'recurring' && hasFirstChargeDifferent && !firstChargeAmount) {
            newErrors.firstChargeAmount = "Informe o valor da primeira cobrança";
        }

        setErrors(newErrors);

        // Se não houver erros, retorna true
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }

        // Validar formulário
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setApiError("");
        setApiSuccess("");

        try {
            // Preparar dados para API
            const offerData = {
                name: formData.name,
                description: formData.description,
                anchoringPrice: formData.anchoringPrice,
                price: formData.price,
                active: formData.status === "active",
                maxInstallments: parseInt(formData.installments),
                paymentMethods: formData.paymentMethods,
                enableFunnel: formData.enableFunnel,
                funnelTags: formData.enableFunnel ? formData.funnelTags : [],
                funnelOrigin: formData.enableFunnel ? formData.funnelOrigin : "",
                funnelMedium: formData.enableFunnel ? formData.funnelMedium : "",
                funnelCampaign: formData.enableFunnel ? formData.funnelCampaign : "",
                funnelTerm: formData.enableFunnel ? formData.funnelTerm : "",
                funnelContent: formData.enableFunnel ? formData.funnelContent : "",
                sck: formData.enableFunnel ? formData.sck : "",
                billingType: billingType,
                recurringFrequency: billingType === 'recurring' ? recurringFrequency : null,
                firstChargeAmount: billingType === 'recurring' && hasFirstChargeDifferent ? parseFloat(firstChargeAmount) : null,
                currency: currency,
                checkoutLanguage: checkoutLanguage,
                settingsData: {
                    ...existingSettingsData,
                    initialInstallments: currency === 'BRL'
                        ? clampInitialInstallments(initialInstallments, parseInt(formData.installments))
                        : INITIAL_INSTALLMENTS_MAX,
                },
            };

            if (productId && offerId) {
                // Atualizar oferta existente
                const response = await axios.put(
                    `${import.meta.env.VITE_API_URL}/api/products/${productId}/offers/${offerId}`,
                    offerData,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${authService.getToken()}`
                        }
                    }
                );

                if (response.status === 200) {
                    setApiSuccess("Oferta atualizada com sucesso!");

                    // Callback de sucesso se fornecido
                    if (onSuccess) {
                        onSuccess(response.data);
                    }
                }
            } else if (productId) {
                // Criar nova oferta
                const response = await axios.post(
                    `${import.meta.env.VITE_API_URL}/api/products/${productId}/offers`,
                    offerData,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${authService.getToken()}`
                        }
                    }
                );

                if (response.status === 201) {
                    setApiSuccess("Oferta criada com sucesso!");

                    // Callback de sucesso se fornecido
                    if (onSuccess) {
                        onSuccess(response.data);
                    }
                }
            }
        } catch (error: any) {
            console.error("Erro ao salvar oferta:", error);
            setApiError(error.response?.data?.message || "Erro ao salvar a oferta. Tente novamente.");

            // Callback de erro se fornecido
            if (onError) {
                onError(error);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="col-span-1 lg:col-span-2">
                <ComponentCard title="Configurações da sua oferta">
                    {fetchLoading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                    ) : (
                        <>
                            {apiError && (
                                <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4">
                                    {apiError}
                                </div>
                            )}

                            {apiSuccess && (
                                <div className="bg-green-50 dark:bg-green-900/10 text-green-600 dark:text-green-400 p-3 rounded-lg mb-4">
                                    {apiSuccess}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="p-6 space-y-6">
                                <div>
                                    <Switch
                                        label="Oferta ativa?"
                                        checked={formData.status === "active"}
                                        onChange={handleSwitchChange}
                                    />
                                </div>

                                <div>
                                    <Switch
                                        label="Ativar trackeamento para essa oferta?"
                                        checked={formData.enableFunnel}
                                        onChange={handleFunnelSwitchChange}
                                    />
                                </div>

                                {formData.enableFunnel && (
                                    <>
                                        <div className="mt-5">
                                            <Label htmlFor="funnelTags">Nome do funil</Label>
                                            <TagsInput
                                                id="funnelTags"
                                                selectedTags={formData.funnelTags}
                                                onChange={handleTagsChange}
                                                placeholder="Digite o nome do funil e pressione Enter"
                                                suggestedTags={funnelOptions}
                                            />
                                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                                Você pode criar novos funis ou selecionar existentes.
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 mt-6">
                                            <div>
                                                <Label htmlFor="funnelOrigin">Origem (utm_source)</Label>
                                                <Input
                                                    type="text"
                                                    id="funnelOrigin"
                                                    name="funnelOrigin"
                                                    value={formData.funnelOrigin}
                                                    onChange={(e) => setFormData({ ...formData, funnelOrigin: e.target.value })}
                                                    placeholder="Ex: Facebook, Instagram, Google Ads..."
                                                />
                                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                                    Informe a origem do tráfego deste funil.
                                                </p>
                                            </div>

                                            <div>
                                                <Label htmlFor="funnelMedium">Canal (utm_medium)</Label>
                                                <Input
                                                    type="text"
                                                    id="funnelMedium"
                                                    name="funnelMedium"
                                                    value={formData.funnelMedium}
                                                    onChange={(e) => setFormData({ ...formData, funnelMedium: e.target.value })}
                                                    placeholder="Ex: cpc, social, email..."
                                                />
                                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                                    Informe o canal do tráfego deste funil.
                                                </p>
                                            </div>

                                            <div>
                                                <Label htmlFor="funnelCampaign">Campanha (utm_campaign)</Label>
                                                <Input
                                                    type="text"
                                                    id="funnelCampaign"
                                                    name="funnelCampaign"
                                                    value={formData.funnelCampaign}
                                                    onChange={(e) => setFormData({ ...formData, funnelCampaign: e.target.value })}
                                                    placeholder="Ex: black_friday, lancamento..."
                                                />
                                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                                    Informe a campanha do tráfego deste funil.
                                                </p>
                                            </div>

                                            <div>
                                                <Label htmlFor="funnelContent">Conteúdo (utm_content)</Label>
                                                <Input
                                                    type="text"
                                                    id="funnelContent"
                                                    name="funnelContent"
                                                    value={formData.funnelContent}
                                                    onChange={(e) => setFormData({ ...formData, funnelContent: e.target.value })}
                                                    placeholder="Ex: banner_topo, link_rodape..."
                                                />
                                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                                    Informe o conteúdo ou posição do link.
                                                </p>
                                            </div>

                                            <div>
                                                <Label htmlFor="funnelTerm">Palavra Chave (utm_term)</Label>
                                                <Input
                                                    type="text"
                                                    id="funnelTerm"
                                                    name="funnelTerm"
                                                    value={formData.funnelTerm}
                                                    onChange={(e) => setFormData({ ...formData, funnelTerm: e.target.value })}
                                                    placeholder="Ex: marketing digital, vendas online..."
                                                />
                                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                                    Informe a palavra chave do tráfego deste funil.
                                                </p>
                                            </div>

                                            <div>
                                                <Label htmlFor="sck">SCK (Sub-Campaign Key)</Label>
                                                <Input
                                                    type="text"
                                                    id="sck"
                                                    name="sck"
                                                    value={formData.sck}
                                                    onChange={(e) => setFormData({ ...formData, sck: e.target.value })}
                                                    placeholder="Ex: promo_natal_2024, desconto_10..."
                                                />
                                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                                    Identificador único da sub-campanha.
                                                </p>
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="mt-5">
                                    <Label htmlFor="inputName">Nome da oferta *</Label>
                                    <Input
                                        type="text"
                                        id="inputName"
                                        placeholder="Informe o nome da sua oferta"
                                        onChange={handleInputChange}
                                        value={formData.name}
                                        error={!!errors.inputName}
                                        hint={errors.inputName}
                                    />
                                </div>

                                <div className="mt-5">
                                    <Label htmlFor="inputDescription">Descrição da oferta *</Label>
                                    <TextArea
                                        value={formData.description}
                                        onChange={handleTextAreaChange}
                                        rows={6}
                                        placeholder="Apenas para seu controle interno. Não é visível para o cliente."
                                        error={!!errors.description}
                                        hint={errors.description}
                                    />
                                </div>

                                <div className="mt-6">
                                    <Label>Tipo de cobrança * <span className="text-xs text-orange-500">(não editável)</span></Label>
                                    <div className="flex flex-row gap-6 mt-5">
                                        <Radio
                                            id="billingType-single"
                                            name="billingType"
                                            value="single"
                                            checked={billingType === 'single'}
                                            label="Única"
                                            onChange={() => { }} // Removido o callback para impedir alteração
                                            disabled={true} // Adicionado disable para tornar somente leitura
                                        />
                                        <Radio
                                            id="billingType-recurring"
                                            name="billingType"
                                            value="recurring"
                                            checked={billingType === 'recurring'}
                                            label="Recorrente"
                                            onChange={() => { }} // Removido o callback para impedir alteração
                                            disabled={true} // Adicionado disable para tornar somente leitura
                                        />
                                    </div>

                                    {billingType === 'recurring' && (
                                        <>
                                            <div className="mt-6">
                                                <Label>Frequência da assinatura * <span className="text-xs text-orange-500">(não editável)</span></Label>
                                                <Select
                                                    options={recurringCycleOptions}
                                                    placeholder="Selecione a frequência"
                                                    onChange={() => { }}
                                                    value={recurringFrequency}
                                                    readOnly={true}
                                                    className={`dark:bg-dark-900 ${errors.recurringFrequency ? 'border-red-500' : ''}`}
                                                />
                                                {errors.recurringFrequency && (
                                                    <p className="mt-1 text-sm text-red-500">{errors.recurringFrequency}</p>
                                                )}
                                            </div>

                                            <div className="mt-6">
                                                <Switch
                                                    label="Valor diferente na primeira cobrança"
                                                    checked={hasFirstChargeDifferent}
                                                    onChange={checked => setHasFirstChargeDifferent(checked)}
                                                />
                                                {hasFirstChargeDifferent && (
                                                    <div className="mt-6">
                                                        <Label>Valor da primeira cobrança *</Label>
                                                        <Input
                                                            type="text"
                                                            placeholder="Ex: 10,00"
                                                            value={firstChargeAmountFormatted}
                                                            onChange={e => {
                                                                // Aqui assume que você usa o formatPrice igual ao preço do produto
                                                                const { formattedValue, numericValue } = formatPrice(e.target.value);
                                                                setFirstChargeAmountFormatted(formattedValue);
                                                                setFirstChargeAmount(numericValue.toString());

                                                                // Limpar o erro quando o usuário digitar
                                                                if (errors.firstChargeAmount) {
                                                                    setErrors({
                                                                        ...errors,
                                                                        firstChargeAmount: ""
                                                                    });
                                                                }
                                                            }}
                                                            error={!!errors.firstChargeAmount}
                                                            hint={errors.firstChargeAmount}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 mt-6">
                                    <div className="mt-5">
                                        <Label htmlFor="inputPrice">Preço da oferta *</Label>
                                        <Input
                                            type="text"
                                            id="inputPrice"
                                            placeholder="0,00"
                                            onChange={handleInputChange}
                                            value={formData.priceFormatted}
                                            error={!!errors.inputPrice}
                                            hint={errors.inputPrice}
                                        />
                                    </div>

                                    <div className="mt-5">
                                        <Label htmlFor="inputAnchoringPrice">Preço de Ancoragem (Opcional)</Label>
                                        <Input
                                            type="text"
                                            id="inputAnchoringPrice"
                                            placeholder="0,00"
                                            onChange={handleInputChange}
                                            value={formData.anchoringPriceFormatted}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 mt-6">
                                    <div>
                                        <Label>Moeda * <span className="text-xs text-orange-500">(não editável)</span></Label>
                                        <Select
                                            options={currencyOptions}
                                            placeholder="Selecione uma opção"
                                            value={currency}
                                            readOnly={true}
                                            onChange={() => { }}
                                            className={`dark:bg-dark-900 ${errors.currency ? 'border-red-500' : ''}`}
                                        />
                                        {errors.currency && <p className="mt-1 text-sm text-red-500">{errors.currency}</p>}
                                    </div>
                                    <div>
                                        <Label>Idioma do Checkout *</Label>
                                        <Select
                                            options={languageOptions}
                                            placeholder="Selecione uma opção"
                                            value={checkoutLanguage}
                                            onChange={(value) => setCheckoutLanguage(value)}
                                            className={`dark:bg-dark-900 ${errors.checkoutLanguage ? 'border-red-500' : ''}`}
                                        />
                                        {errors.checkoutLanguage && <p className="mt-1 text-sm text-red-500">{errors.checkoutLanguage}</p>}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 mt-6">
                                    <div>
                                        <Label>Forma de Pagamento *</Label>
                                        <Select
                                            options={productPaymentMethods}
                                            placeholder="Selecione uma opção"
                                            onChange={handlePaymentMethodChange}
                                            value={formData.paymentMethod || ''}
                                            className={`dark:bg-dark-900 ${errors.paymentMethod ? 'border-red-500' : ''}`}
                                        />
                                        {errors.paymentMethod && <p className="mt-1 text-sm text-red-500">{errors.paymentMethod}</p>}
                                    </div>

                                    <div>
                                        <Label>Parcelamento *</Label>
                                        <Select
                                            options={productPaymentInstallments}
                                            placeholder="Selecione uma opção"
                                            onChange={handleInstallmentsChange}
                                            value={formData.installments}
                                            className={errors.installments ? "border-red-500" : ""}
                                        />
                                        {errors.installments && <p className="mt-1 text-sm text-red-500">{errors.installments}</p>}
                                    </div>
                                </div>

                                {currency === 'BRL' && parseInt(formData.installments) > 1 && (
                                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 mt-6">
                                        <InitialInstallmentSelect
                                            maxInstallments={parseInt(formData.installments)}
                                            value={initialInstallments}
                                            onChange={setInitialInstallments}
                                        />
                                    </div>
                                )}

                                <div className="mt-8 flex justify-between gap-4.5">
                                    <Button
                                        variant="outline"
                                        size="md"
                                        onClick={handleGoBack}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        size="md"
                                        variant="primary"
                                        onClick={() => handleSubmit()}
                                        disabled={loading}
                                    >
                                        {loading ? "Salvando..." : "Salvar"}
                                    </Button>
                                </div>
                            </form>
                        </>
                    )}
                </ComponentCard>
            </div>
        </div>
    );
}
